import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft, FileX } from 'lucide-react';
import { AppLayout } from '../components/AppLayout';
import { useBranding } from '../contexts/BrandingContext';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface FieldDefinition {
  key: string;
  label: string;
  type: string;
  required?: boolean;
  options?: string[];
}

interface ContentTypeSchema {
  id: string;
  name: string;
  slug: string;
  fieldsJson: FieldDefinition[];
}

interface EntryData {
  id: string;
  title: string;
  slug: string | null;
  dataJson: Record<string, unknown>;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface EntryDetailResponse {
  entry: EntryData;
  contentType: ContentTypeSchema;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getTenantSlug(): string {
  const envSlug = import.meta.env.VITE_TENANT_SLUG as string | undefined;
  if (envSlug) return envSlug;
  const host = window.location.hostname;
  const parts = host.split('.');
  if (parts.length >= 2 && parts[0] !== 'localhost') return parts[0];
  return 'tenant1';
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
}

// ─── Field value renderer ─────────────────────────────────────────────────────

function FieldValue({ field, value }: { field: FieldDefinition; value: unknown }) {
  if (value === undefined || value === null || value === '') return null;

  switch (field.type) {
    case 'boolean':
      if (!value) return null;
      return (
        <div className="flex items-center gap-2 text-sm text-zinc-700">
          <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
          <span>{field.label}</span>
        </div>
      );

    case 'rich_text':
      return (
        <div
          className="prose prose-sm max-w-none text-zinc-700"
          dangerouslySetInnerHTML={{ __html: String(value) }}
        />
      );

    case 'image':
      return (
        <div>
          <img
            src={String(value)}
            alt={field.label}
            className="w-full rounded-xl object-cover"
            loading="lazy"
          />
        </div>
      );

    case 'color':
      return (
        <div className="flex items-center gap-2">
          <div
            className="w-5 h-5 rounded-full border border-zinc-200 flex-shrink-0"
            style={{ backgroundColor: String(value) }}
          />
          <span className="text-sm text-zinc-500 font-mono">{String(value)}</span>
        </div>
      );

    case 'number':
      return (
        <div className="text-sm text-zinc-700">
          <span className="font-medium text-zinc-500">{field.label}: </span>
          {String(value)}
        </div>
      );

    case 'date':
    case 'datetime': {
      const d = new Date(String(value));
      const formatted = isNaN(d.getTime())
        ? String(value)
        : field.type === 'date'
          ? d.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })
          : d.toLocaleString('en-GB');
      return (
        <div className="text-sm text-zinc-700">
          <span className="font-medium text-zinc-500">{field.label}: </span>
          {formatted}
        </div>
      );
    }

    case 'array': {
      const items = Array.isArray(value) ? value : [];
      if (items.length === 0) return null;
      return (
        <div className="space-y-1">
          <p className="text-xs font-medium text-zinc-500 uppercase tracking-wide">{field.label}</p>
          <ul className="list-disc list-inside space-y-0.5 text-sm text-zinc-700">
            {items.map((item, i) => (
              <li key={i}>{String(item)}</li>
            ))}
          </ul>
        </div>
      );
    }

    case 'select':
    case 'text':
    default:
      return <div className="text-sm text-zinc-700">{String(value)}</div>;
  }
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export function CollectionEntryPage() {
  const { typeSlug, slug } = useParams<{ typeSlug: string; slug: string }>();
  const navigate = useNavigate();
  const branding = useBranding();

  const [result, setResult] = useState<EntryDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!typeSlug || !slug) return;
    setLoading(true);
    setNotFound(false);

    fetch(
      `/customer/portal/collections/${encodeURIComponent(typeSlug)}/entries/${encodeURIComponent(slug)}`,
      {
        headers: {
          'x-tenant-slug': getTenantSlug(),
          ...(branding.defaultSpaceId ? { 'x-space-id': branding.defaultSpaceId } : {}),
        },
      },
    )
      .then(async (res) => {
        if (res.status === 404) {
          setNotFound(true);
          return;
        }
        if (!res.ok) throw new Error('Failed to load entry');
        const data = (await res.json()) as EntryDetailResponse;
        setResult(data);

        // Update document title
        document.title = `${data.entry.title} — ${branding.name}`;
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));

    return () => {
      document.title = branding.name;
    };
  }, [typeSlug, slug, branding.name]);

  // ── 404 state ───────────────────────────────────────────────────────────────
  if (notFound) {
    return (
      <AppLayout>
        <div className="flex flex-col items-center justify-center py-24 text-center gap-4">
          <FileX size={40} className="text-zinc-300" />
          <div>
            <h1 className="text-xl font-semibold text-zinc-800">Page not found</h1>
            <p className="text-sm text-zinc-500 mt-1">
              The entry you&apos;re looking for doesn&apos;t exist or has been removed.
            </p>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-2 flex items-center gap-1.5 text-sm font-medium text-zinc-600 hover:text-zinc-900 transition-colors"
          >
            <ArrowLeft size={14} />
            Back to home
          </button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 size={24} className="animate-spin text-zinc-400" />
        </div>
      ) : result ? (
        <article className="max-w-2xl mx-auto space-y-6">
          {/* Breadcrumb */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-800 transition-colors"
          >
            <ArrowLeft size={14} />
            {result.contentType.name}
          </button>

          {/* Header */}
          <header className="space-y-2">
            <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">
              {result.contentType.name}
            </p>
            <h1 className="text-2xl font-bold text-zinc-900 leading-snug">{result.entry.title}</h1>
            {result.entry.publishedAt && (
              <p className="text-sm text-zinc-400">{formatDate(result.entry.publishedAt)}</p>
            )}
          </header>

          {/* Divider */}
          <div className="border-t border-zinc-100" />

          {/* Dynamic field values */}
          {result.contentType.fieldsJson.length > 0 ? (
            <div className="space-y-5">
              {result.contentType.fieldsJson.map((field) => {
                const value = result.entry.dataJson[field.key];
                const rendered = <FieldValue key={field.key} field={field} value={value} />;
                // If FieldValue returns null (empty/falsy), skip the wrapper too
                if (value === undefined || value === null || value === '') return null;
                if (field.type === 'boolean' && !value) return null;
                if (field.type === 'array' && Array.isArray(value) && value.length === 0)
                  return null;

                // For rich_text and image, render without label header
                if (field.type === 'rich_text' || field.type === 'image') {
                  return <div key={field.key}>{rendered}</div>;
                }

                // For boolean, the FieldValue renders its own label
                if (field.type === 'boolean') {
                  return <div key={field.key}>{rendered}</div>;
                }

                // All other fields: label above, value below
                return (
                  <div key={field.key} className="space-y-1">
                    <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wide">
                      {field.label}
                    </p>
                    {rendered}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-zinc-400 text-center py-8">No content fields defined.</p>
          )}
        </article>
      ) : null}
    </AppLayout>
  );
}
